import { ApiSanctionEntityType } from '../api';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import Notes from './sections/Notes';
import { Documents } from '../common/documents/Documents';
import { InternalApiUniversalDocumentEntityType } from '../api-internal';
import PersonAlerts from './sections/PersonAlerts';
import Transactions from './sections/Transactions';
import PersonAttributes from './sections/PersonAttributes';
import Status from './sections/Status';
import TransactionsSummary from './transactions-summary/TransactionsSummary';
import Risk from '../risk/Risk';
import PersonScreeningAlerts from './sections/PersonScreeningAlerts';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { getPersonDisplayName, isCounterparty } from '../common/helper';
import { useQueryAttributeMappings } from '../schema/mappings/mappingsActions';
import {
  CAN_MODIFY_ALERT_THROUGH_FILTER,
  CAN_READ_ALERT,
  CAN_READ_CASE,
  CAN_READ_ENTITY_GROUP,
  CAN_READ_RISK,
  CAN_READ_SANCTION_ALERT,
} from '../auth/permissions';
import Has from '../auth/Has';
import { SnoozedScenarios } from './sections/SnoozedScenarios';
import PersonBridgeAlerts from './sections/PersonBridgeAlerts';
import { InternalApiCaseAttachmentType, InternalApiEntityType, InternalApiPerson } from '../api-internal';
import { useMe } from '../organisation/useMe';
import MatchingSegments from './sections/MatchingSegments';
import PersonManualAlerts from './sections/PersonManualAlerts';
import AttachmentCases from '../case/attachments/AttachmentCases';

interface PersonInfoProps {
  person: InternalApiPerson;
}

export const PersonInfo = ({ person }: PersonInfoProps) => {
  const personId = person !== null ? person.id || null : null;
  if (personId === null) {
    return null;
  }
  const { features } = useMe();
  const { data: attributeMappings } = useQueryAttributeMappings();

  const personName = attributeMappings?.map ? getPersonDisplayName(person, attributeMappings.map) : undefined;

  const isMonitoringEnabled = isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features);
  const isScreeningEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features);
  const isBridgeEnabled =
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features) ||
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);
  const isRiskEnabled = isFeatureEnabled(ORGANISATION_FEATURE.RISK, features);

  return (
    <>
      <Row>
        <Col md={4}>
          <PersonAttributes person={person} />
        </Col>
        <Col md={8}>
          {isCounterparty(person) ? (
            <>
              <Status person={person} />
              {isMonitoringEnabled && (
                <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                  <PersonAlerts personId={personId} />
                </Has>
              )}
              <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                <PersonManualAlerts personId={personId} personName={personName} />
              </Has>
              {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
                <Has allPermissions={[CAN_READ_CASE]}>
                  <AttachmentCases attachmentType={InternalApiCaseAttachmentType.PERSON} externalId={personId} />
                </Has>
              )}
              <Notes person={person} />
              <Documents
                className='universal-documents--legacy'
                entityType={InternalApiUniversalDocumentEntityType.PERSON}
                entityRef={personId}
              />
              {!isFeatureEnabled(ORGANISATION_FEATURE.NO_TRANSACTION_SUMMARY, features) &&
                isFeatureEnabled(ORGANISATION_FEATURE.TRANSACTION_SUMMARY_V2, features) && (
                  <TransactionsSummary personId={personId} isCounterparty={true} />
                )}
            </>
          ) : (
            <>
              <Status person={person} />
              <Has allPermissions={CAN_READ_ENTITY_GROUP}>
                <MatchingSegments entityId={person.id} entityType={InternalApiEntityType.PERSON} />
              </Has>
              {isRiskEnabled && (
                <Has allPermissions={CAN_READ_RISK}>
                  <Risk personId={personId} />
                </Has>
              )}
              {isMonitoringEnabled && <SnoozedScenarios personId={personId} />}
              {isMonitoringEnabled && (
                <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                  <PersonAlerts personId={personId} />
                </Has>
              )}
              {isScreeningEnabled && (
                <Has anyPermissions={[CAN_READ_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                  <PersonScreeningAlerts personId={personId} entityType={ApiSanctionEntityType.PERSON} />
                </Has>
              )}
              {isScreeningEnabled && (
                <Has anyPermissions={[CAN_READ_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                  <PersonScreeningAlerts personId={personId} entityType={ApiSanctionEntityType.TRANSACTION} />
                </Has>
              )}
              {isBridgeEnabled && (
                <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                  <PersonBridgeAlerts personId={personId} />
                </Has>
              )}
              <Has anyPermissions={[CAN_READ_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
                <PersonManualAlerts personId={personId} personName={personName} />
              </Has>
              {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
                <Has allPermissions={[CAN_READ_CASE]}>
                  <AttachmentCases attachmentType={InternalApiCaseAttachmentType.PERSON} externalId={personId} />
                </Has>
              )}
              <Notes person={person} />
              <Documents
                className='universal-documents--legacy'
                entityType={InternalApiUniversalDocumentEntityType.PERSON}
                entityRef={personId}
              />
              {!isFeatureEnabled(ORGANISATION_FEATURE.NO_TRANSACTION_SUMMARY, features) && (
                <TransactionsSummary personId={personId} isCounterparty={false} />
              )}
            </>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Transactions personId={personId} />
        </Col>
      </Row>
    </>
  );
};

export default PersonInfo;
